import { useContext, useMemo } from "react";
import { TransactionRuleManagementContext } from "@components/transactionRules/TransactionRuleManagementContext/TransactionRuleManagement.context";
import { RuleGroupType, RuleSource } from "@common/types/transactionRule";
import { FiltersTypes } from "@common/types/filters/general";
import { useOverlays } from "contexts/OverlayContext";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { useEntities } from "@hooks/useEntities";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useIsAccrualLedgerEnabled } from "@hooks/useIsAccrualLedgerEnabled";
import { useClasses } from "@api/class.api";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { TablePageHeader } from "../../pageHeader/TablePageHeader";
import { FiltersChangeFn, FilterTabs } from "../../pageHeader/filters/lib";
import {
    isCategoryFilterApplicableTab,
    isCounterpartyOrClassFilterApplicableTab,
} from "./utils";
import { RuleValueFilters } from "./ruleValueFilters";
import { WithHotkey } from "@design-system/Button/WithHotkey";

interface Props {
    setSearch: (search: string) => void;
    search: string;
    filters: Pick<
        FiltersTypes,
        | "entityIds"
        | "ruleStatuses"
        | "category"
        | "ruleSources"
        | "ruleActionTypes"
    > &
        RuleValueFilters;
    onFiltersChange: FiltersChangeFn;
    navigation: React.ReactNode;
    activeTab: RuleGroupType;
}

export const RulesPageHeader: React.FC<Props> = ({
    setSearch,
    search,
    filters,
    onFiltersChange,
    navigation,
    activeTab,
}) => {
    const { openOverlay } = useOverlays();
    const { createRuleGroup } = useContext(TransactionRuleManagementContext);
    const { categoryGlMapping, enableSimilarityRules } = useTypedFlags();
    const { isActivated: isClassesEnabled } = useClasses();
    const isAccrualLedgerEnabled = useIsAccrualLedgerEnabled();
    const entities = useEntities();

    // The rules page stores "all entities" as an empty list; the ledger
    // pickers read an empty list as "none selected", so hand them every
    // entity in that case.
    const overlayFilters = useMemo(
        () =>
            filters.entityIds?.length
                ? filters
                : {
                      ...filters,
                      entityIds: entities.map((entity) => entity.id),
                  },
        [filters, entities],
    );

    const isCategoryFilterVisible =
        categoryGlMapping && isCategoryFilterApplicableTab(activeTab);
    // Both Kick options are unsatisfiable without the Kick group, which
    // `RulesTable` renders only under `enableSimilarityRules`.
    const isSourceFilterVisible =
        enableSimilarityRules && activeTab === RuleGroupType.TRANSACTION;

    const isActionFilterVisible = activeTab === RuleGroupType.TRANSACTION;

    const isCounterpartyFilterVisible =
        isCounterpartyOrClassFilterApplicableTab(activeTab);
    const isClassFilterVisible =
        isCounterpartyFilterVisible && isClassesEnabled;

    const availableFilters = [
        FilterTabs.RULES_STATUS,
        ...(isCategoryFilterVisible ? [FilterTabs.CATEGORY] : []),
        ...(isSourceFilterVisible ? [FilterTabs.RULES_SOURCE] : []),
        ...(isActionFilterVisible ? [FilterTabs.RULES_ACTION] : []),
        ...(isCounterpartyFilterVisible ? [FilterTabs.COUNTERPARTY] : []),
        ...(isClassFilterVisible ? [FilterTabs.CLASS] : []),
        FilterTabs.RULES_REVIEW,
        FilterTabs.LEDGER,
        ...(isAccrualLedgerEnabled ? [FilterTabs.ACCRUAL_LEDGER] : []),
    ];

    return (
        <TablePageHeader
            search={search}
            onSearchChange={(value) => setSearch(value ?? "")}
            filters={overlayFilters}
            onFiltersChange={onFiltersChange}
            availableFilters={availableFilters}
            ledgerFilterLabel="Cash ledger"
            valuePickersOnly
            isFullPage
            tabNavigationTop={navigation}
        >
            <EntityFilterCombobox
                filters={filters}
                onChange={onFiltersChange}
            />
            <HStack gap={8}>
                <Button
                    variant="primary"
                    size="md"
                    onClick={() => {
                        createRuleGroup(activeTab);
                    }}
                >
                    Add rule group
                </Button>
                <WithHotkey
                    hotKey="alt+n"
                    hotKeyOptions={{
                        description: "Create rule",
                    }}
                >
                    <Button
                        variant="primary"
                        size="md"
                        onClick={() =>
                            openOverlay("createRule", {
                                source: RuleSource.SETTINGS,
                                groupType: activeTab,
                            })
                        }
                    >
                        Add rule
                    </Button>
                </WithHotkey>
            </HStack>
        </TablePageHeader>
    );
};
